import { AuthenticationMethod } from "./../../../shared/enums/authentication-provider.enum";
import { inject, Injectable, NgZone } from "@angular/core";
import { FirebaseService } from "../firebase/firebase.service";
import { UserCredential } from "@angular/fire/auth";
import { Router } from "@angular/router";
import { updateProfile } from "@angular/fire/auth";
import {
  catchError,
  distinctUntilChanged,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { HttpClient } from "@angular/common/http";
import { User as FirebaseUser } from "@angular/fire/auth";
import { LogService } from "../../../shared/service/logging/log.service";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  firebase = inject(FirebaseService);
  router = inject(Router);
  http = inject(HttpClient);
  zone = inject(NgZone);
  logger = inject(LogService);

  public readonly isAuthenticated$: Observable<boolean> =
    this.firebase.firebaseUser$.pipe(
      map((user) => !!user),
      distinctUntilChanged(),
      tap((val) => this.logger.debug("isAuthenticated: ", val)),
    );

  public readonly firebaseUserInfo$: Observable<FirebaseUser> =
    this.firebase.firebaseUser$.pipe(distinctUntilChanged());

  public readonly idToken$: Observable<string> = this.firebase.idToken$.pipe(
    distinctUntilChanged(),
  );

  public readonly isLoggedIn$: Observable<boolean> = this.idToken$.pipe(
    switchMap((token) => {
      if (!token) return of(false);

      return this.firebaseUserInfo$.pipe(
        map((user) => {
          const isExternaLogin = user.providerData.some(
            (provider) => provider.providerId === AuthenticationMethod.External,
          );
          if (isExternaLogin) {
            this.setSyncFlag(true);
          }

          return isExternaLogin ? !!user.emailVerified : true;
        }),
      );
    }),
    distinctUntilChanged(),
  );

  public readonly currentSessionAuth$: Observable<boolean> =
    this.firebase.userCredential$.pipe(
      startWith(null),
      pairwise(),
      map(([prev, curr]) => {
        const credentialChanged = !!curr?.user && curr !== prev;
        return credentialChanged || this.shouldSync();
      }),
      distinctUntilChanged(),
    );

  public readonly isLoading$: Observable<boolean> = this.isLoggedIn$.pipe(
    map(() => false),
    startWith(true),
    distinctUntilChanged(),
    tap((val) => this.logger.debug("isLoading: ", val)),
    shareReplay(1),
  );

  public readonly msfAccessToken$: Observable<string | null> =
    this.firebase.msfAccessToken$.pipe(distinctUntilChanged(), shareReplay(1));

  public readonly firebaseUserInfo = toSignal(this.firebaseUserInfo$, {
    initialValue: undefined,
  });

  public readonly idToken = toSignal(this.idToken$, {
    initialValue: undefined,
  });

  public readonly isLoggedIn = toSignal(this.isLoggedIn$, {
    initialValue: false,
  });

  public readonly isLoading = toSignal(this.isLoading$, {
    initialValue: true,
  });

  public registerExternal(
    email: string,
    firstName: string,
    lastName: string,
    password: string,
  ): Observable<UserCredential> {
    // const displayName = `${firstName} ${lastName}`;
    const displayName = `${lastName}, ${firstName}`;
    return this.firebase.register(email, displayName, password).pipe(
      tap(() => this.logger.debug("User registered with email: ", email)),
      catchError((err) => {
        this.logger.error("Error during registration: ", err);
        throw err;
      }),
    );
  }

  public loginExternal(
    email: string,
    password: string,
  ): Observable<UserCredential> {
    return this.firebase.login(email, password).pipe(
      tap(() =>
        this.logger.debug("User logged in successfully with email: ", email),
      ),
      catchError((err) => {
        this.logger.error("Error during login: ", err);
        throw err;
      }),
    );
  }

  public loginMicrosoftPopup(): Observable<string | null> {
    return this.firebase.loginMicrosoftPopup().pipe(
      tap(() =>
        this.logger.debug("Microsoft login popup processed successfully"),
      ),
      catchError((err) => {
        this.logger.error("Error during Microsoft login:", err);
        throw err;
      }),
    );
  }

  public loginMicrosoftRedirect(): void {
    this.firebase.loginMicrosoftRedirect();
  }

  public handleMicrosoftRedirectResult(): Observable<string | null> {
    return this.firebase.handleRedirectResult().pipe(
      tap(() => this.logger.debug("Microsoft login redirect result handled")),
      catchError((err) => {
        this.logger.error("Error handling Microsoft redirect login:", err);
        throw err;
      }),
    );
  }

  public logout(): void {
    this.firebase
      .logout()
      .pipe(tap(() => this.logger.debug("Logging user out...")))
      .subscribe(() => {
        localStorage.removeItem("lastVisited");
        this.router.navigateByUrl("/");
        location.reload();
      });
  }

  public updateUsername(newUsername: string) {
    return this.firebase.updateUsername(newUsername);
  }

  public redirectToLastVisited() {
    const lastVisitedUrl = localStorage.getItem("lastVisited") || "/";
    this.router.navigateByUrl(lastVisitedUrl);
  }

  public redirectToCallback(): void {
    this.router.navigateByUrl("/callback");
  }

  public redirectToRegister(): void {
    this.router.navigateByUrl("/account/register");
  }

  public redirectToLogin(): void {
    this.router.navigateByUrl("/account/login");
  }

  public shouldSync(): boolean {
    const val = localStorage.getItem("auth-sync-pending") === "true";
    return val;
  }

  public setSyncFlag(syncPending: boolean): void {
    if (syncPending) {
      localStorage.setItem("auth-sync-pending", "true");
    } else {
      localStorage.removeItem("auth-sync-pending");
    }
  }
}
